import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

interface Row {
  id: string;
  zone: string;
  from_location: string;
  to_location: string;
  price_1_3: number | null;
  price_4_7: number | null;
  price_8_17: number | null;
  currency: string;
  is_new: boolean;
  show_on_home: boolean;
  position: number;
  is_active: boolean;
}

const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));

const AdminTransferPricing = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [zoneFilter, setZoneFilter] = useState<string>("all");

  const load = async () => {
    const { data, error } = await supabase
      .from("transfer_fares")
      .select("*")
      .order("position", { ascending: true });
    if (error) toast.error("Erreur: " + error.message);
    else setRows((data as Row[]) || []);
  };

  useEffect(() => {
    load();
  }, []);

  const zones = Array.from(new Set(rows.map((r) => r.zone).filter(Boolean)));

  const update = (id: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const save = async (r: Row) => {
    setSaving(r.id);
    const { error } = await supabase
      .from("transfer_fares")
      .update({
        zone: r.zone,
        from_location: r.from_location,
        to_location: r.to_location,
        price_1_3: r.price_1_3,
        price_4_7: r.price_4_7,
        price_8_17: r.price_8_17,
        currency: r.currency || "EUR",
        is_new: r.is_new,
        show_on_home: r.show_on_home,
        position: r.position,
        is_active: r.is_active,
      })
      .eq("id", r.id);
    if (error) toast.error("Erreur: " + error.message);
    else toast.success("Tarif enregistré");
    setSaving(null);
  };

  const addRow = async () => {
    const nextPos = rows.reduce((max, r) => Math.max(max, r.position), 0) + 1;
    const { data, error } = await supabase
      .from("transfer_fares")
      .insert({
        zone: zoneFilter !== "all" ? zoneFilter : "Marrakech",
        from_location: "",
        to_location: "",
        currency: "EUR",
        position: nextPos,
      })
      .select()
      .single();
    if (error) {
      toast.error("Erreur: " + error.message);
      return;
    }
    setRows((prev) => [...prev, data as Row]);
    toast.success("Ligne ajoutée");
  };

  const removeRow = async (r: Row) => {
    if (!confirm(`Supprimer ${r.from_location} → ${r.to_location} ?`)) return;
    const { error } = await supabase.from("transfer_fares").delete().eq("id", r.id);
    if (error) toast.error("Erreur: " + error.message);
    else {
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Supprimé");
    }
  };

  const move = async (r: Row, dir: -1 | 1) => {
    const sorted = [...rows].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((x) => x.id === r.id);
    const target = sorted[idx + dir];
    if (!target) return;
    const a = { ...r, position: target.position };
    const b = { ...target, position: r.position };
    setRows((prev) => prev.map((x) => (x.id === a.id ? a : x.id === b.id ? b : x)));
    await supabase.from("transfer_fares").update({ position: a.position }).eq("id", a.id);
    await supabase.from("transfer_fares").update({ position: b.position }).eq("id", b.id);
  };

  const visible = rows
    .filter((r) => zoneFilter === "all" || r.zone === zoneFilter)
    .sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold">Tarifs transferts</h2>
        <div className="flex items-center gap-2">
          <select
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">Toutes les zones</option>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
          <Button onClick={addRow} size="sm">
            <Plus className="w-4 h-4 mr-1" /> Ajouter un trajet
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Chaque trajet a 3 prix selon le nombre de passagers. « Afficher sur l'accueil » place le
        trajet dans le tableau de la page d'accueil ; tous les trajets actifs apparaissent sur la
        page Tarifs. Laisser un prix vide affiche « — ».
      </p>

      <div className="space-y-4">
        {visible.map((r, idx) => (
          <Card key={r.id} className="border-border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
              <CardTitle className="font-heading text-base">
                {r.from_location || "—"} → {r.to_location || "—"}
              </CardTitle>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(r, -1)} disabled={idx === 0}>
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => move(r, 1)}
                  disabled={idx === visible.length - 1}
                >
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => removeRow(r)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Zone (groupe)</label>
                  <Input value={r.zone} onChange={(e) => update(r.id, { zone: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Départ</label>
                  <Input
                    value={r.from_location}
                    onChange={(e) => update(r.id, { from_location: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Arrivée</label>
                  <Input
                    value={r.to_location}
                    onChange={(e) => update(r.id, { to_location: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">1-3 places</label>
                  <Input
                    type="number"
                    value={r.price_1_3 ?? ""}
                    onChange={(e) => update(r.id, { price_1_3: numOrNull(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">4-7 places</label>
                  <Input
                    type="number"
                    value={r.price_4_7 ?? ""}
                    onChange={(e) => update(r.id, { price_4_7: numOrNull(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">8-17 places</label>
                  <Input
                    type="number"
                    value={r.price_8_17 ?? ""}
                    onChange={(e) => update(r.id, { price_8_17: numOrNull(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Devise</label>
                  <Input
                    value={r.currency}
                    onChange={(e) => update(r.id, { currency: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm font-body">
                  <input
                    type="checkbox"
                    checked={r.is_active}
                    onChange={(e) => update(r.id, { is_active: e.target.checked })}
                  />
                  Actif (visible sur le site)
                </label>
                <label className="flex items-center gap-2 text-sm font-body">
                  <input
                    type="checkbox"
                    checked={r.show_on_home}
                    onChange={(e) => update(r.id, { show_on_home: e.target.checked })}
                  />
                  Afficher sur l'accueil
                </label>
                <label className="flex items-center gap-2 text-sm font-body">
                  <input
                    type="checkbox"
                    checked={r.is_new}
                    onChange={(e) => update(r.id, { is_new: e.target.checked })}
                  />
                  Badge « Nouveau »
                </label>
              </div>

              <Button onClick={() => save(r)} disabled={saving === r.id} className="w-full" size="sm">
                {saving === r.id ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminTransferPricing;
